import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ExcelJS from "exceljs";

import WPButton from "../../packages/WPButton";
import WPLoader from "../../packages/WPLoader";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { useToastNotification } from "../../packages/WPToastNotification";

import Header from "../../components/Header";
import PageNavigation from "../../components/PageNavigation";
import TrainSelector from "../../components/TrainSelector";
import {
  createProcessedDataExcelSheet,
  processCleanDataForExcel,
} from "../../XLSDownload/ExportAllTables/processedDataSheet";

import {
  APP_SCOPE,
  NORMALIZATION_TYPES,
} from "../../constants/global.constants";

import cleaningIcon from "../../assets/cleaning_advisor_logo.svg";
import { DownloadIcon, InfoCircle } from "../../assets/icons";
import replacementIcon from "../../assets/replacement_advisor_logo.svg";

import Footer from "../../common/footer/Footer";

import {
  extractPlantId,
  generateFileName,
  getSelectedPlantTrainInfo,
  saveExcelFile,
} from "../../utils/Utils";

import { cleanedData, getLastFileDetails } from "../../network/cipAPI";

import {
  storePlantDetails,
  updateSelectedTrain,
} from "../../Store/TemplateStore/template.action";
import CAStore from "../../Store/TemplateStore/template.store";

import "./index.scss";

export const store = CAStore;
const basePath = "/"; //isLocalhost ? "/" : window.location.origin;
const Home = () => {
  const { t } = useTranslation();
  const plantId = extractPlantId();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, plantData } = useSelector((state) => ({
    token: state.token,
    plantData: state.plantData,
  }));
  const [isLoading, setIsLoading] = useState(true);
  const [normAppliedType, setNormAppliedType] = useState();
  const hasLoaded = useRef(false);
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  useEffect(() => {
    if (token && !hasLoaded.current) {
      hasLoaded.current = true;
      getLastFileDetail();
    } else {
      setIsLoading(false);
    }
  }, [token, plantData]);

  const getLastFileDetail = async () => {
    try {
      const trainToUse = getSelectedPlantTrainInfo(plantData, plantId);
      const fileDetails = await getLastFileDetails(trainToUse, {
        overrideAppScope: APP_SCOPE.CA,
      });
      if (fileDetails && fileDetails?.data) {
        const { norm_applied_type, num_trains, name, water_type, sel_train } =
          fileDetails.data;
        setNormAppliedType(norm_applied_type);
        dispatch(
          storePlantDetails({
            num_trains,
            plantName: name,
            water_type,
          }),
        );
        dispatch(updateSelectedTrain(sel_train));
      }
    } catch (err) {
      hasLoaded.current = false;
      showErrorNotification(
        err?.message || t("appHome.unableToFetchFileDetails"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToCleaningAdvisor = () => {
    navigate(`${basePath}cleaningAdvisorHome?plantId=${plantId}`);
  };

  const navigateToLifetimeAdvisor = () => {
    navigate(`${basePath}lifetimeAdvisorHome?plantId=${plantId}`);
  };

  const handleDownloadNormalizedData = async () => {
    try {
      setIsLoading(true);
      const trainToUse = getSelectedPlantTrainInfo(plantData, plantId);
      const response = await cleanedData(trainToUse);

      if (response?.data?.cleaned_data) {
        const data = JSON.parse(response.data.cleaned_data);
        const data_units = response.data.units;
        // Process the data for Excel
        const processedResult = processCleanDataForExcel(data, data_units);
        if (
          processedResult &&
          processedResult.data &&
          processedResult.data.length > 0
        ) {
          // Create Excel workbook
          const workbook = new ExcelJS.Workbook();
          // Create the normalized data sheet
          createProcessedDataExcelSheet(
            workbook,
            processedResult.data,
            t("appHome.normalizedData"),
            t,
            processedResult.units,
          );

          const filename = generateFileName(t("appHome.normalizedData"));
          await saveExcelFile(filename, workbook);

          showSuccessNotification(t("appHome.normalizedDataDownloaded"));
        } else {
          showErrorNotification(t("appHome.noDataAvailable"));
        }
      } else {
        showErrorNotification(t("appHome.failedToFetchData"));
      }
    } catch (err) {
      showErrorNotification(err?.message || t("appHome.downloadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToDataUpload = () => {
    navigate(`${basePath}analyseData?plantId=${plantId}&isEdit=true`);
  };

  return (
    <>
      <div className="container-main">
        <Header
          showPlantMenu
          hideCosts
          navigationConfig={{
            onPreviousClick: handleBackToDataUpload,
            onNextClick: navigateToCleaningAdvisor,
          }}
        />
        <PageNavigation />
        <div className="main-container">
          <div className="content-container">
            {isLoading ? (
              <WPLoader />
            ) : (
              <div className="analyze-plant-wrapper">
                <div className="analyze-plant-section">
                  <div className="analyze-plant-content">
                    <div className="heading-text-new">
                      {t("appHome.analyseYourPlant")}
                    </div>
                    <div className="analyze-plant-description">
                      {t("appHome.analyseYourPlantDescription")}
                    </div>
                  </div>
                  <TrainSelector app="ca" />
                </div>

                <div className="advisor-list-section">
                  <div className="advisor-card">
                    <div className="advisor-card-content">
                      <img
                        className="advisor-icon"
                        src={cleaningIcon}
                        alt="Cleaning Advisor"
                      ></img>
                      <div className="advisor-info">
                        <div className="advisor-title">
                          {t("appHome.roAdvisor")}
                        </div>
                        <div className="advisor-description">
                          {t("appHome.roAdvisorDescription")}
                        </div>
                      </div>
                    </div>
                    <WPButton
                      onClick={navigateToCleaningAdvisor}
                      variant="contained"
                      type="primary"
                    >
                      {t("appHome.gotoApp")}
                    </WPButton>
                  </div>

                  <div className="advisor-card">
                    <div className="advisor-card-content">
                      <img
                        className="advisor-icon"
                        src={replacementIcon}
                        alt="Replacement Advisor"
                      ></img>
                      <div className="advisor-info">
                        <div className="advisor-title">
                          {t("appHome.roReplacementAdvisor")}
                        </div>
                        <div className="advisor-description">
                          {t("appHome.roReplacementAdvisorDescription")}
                        </div>
                      </div>
                    </div>
                    <WPButton
                      onClick={navigateToLifetimeAdvisor}
                      variant="contained"
                      type="primary"
                    >
                      {t("appHome.gotoApp")}
                    </WPButton>
                  </div>
                </div>
                {normAppliedType && (
                  <div className="advance-settings-note">
                    <div className="advance-settings-note__icon">
                      <InfoCircle />
                    </div>
                    <div className="advance-settings-note__text">
                      {normAppliedType === NORMALIZATION_TYPES.FT_NORM_PRO ||
                      normAppliedType === NORMALIZATION_TYPES.FT_NORM_FALLBACK
                        ? t("appHome.normalizedWithFTNorm")
                        : t("appHome.normalizedWithoutFTNorm")}
                    </div>
                    <WPTertiaryButton
                      id="download-normalized-data-btn"
                      label={t("appHome.downloadNormalizedData")}
                      iconPlacement="before"
                      icon={<DownloadIcon />}
                      size="small"
                      variant="text"
                      className="download-normalized-btn"
                      onClick={handleDownloadNormalizedData}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default Home;
